// Live coaching intervention resolver.
//
// Evaluates incoming telemetry on a fixed 1000ms cadence. When the user's
// live heart rate runs >= 15 BPM above the target threshold configured for the
// active movement profile, it fires a non-destructive intervention callback
// with an adaptive suggestion. "Non-destructive" = it never stops the stream
// or mutates session data; it only surfaces a recommendation.

import type { MachineKind, TelemetryFrame } from './types'

export const HR_INTERVENTION_DELTA_BPM = 15
const EVAL_INTERVAL_MS = 1000

export type InterventionEvent = {
  kind: 'cardio_strain_high'
  observedHr: number
  targetHr: number
  deltaBpm: number
  suggestion: string
  machineKind: MachineKind
  firedAt: number
}

type InterventionListener = (event: InterventionEvent) => void

function suggestionForMachine(kind: MachineKind): string {
  switch (kind) {
    case 'treadmill':
      return 'Cardio Strain Running High. Drop treadmill incline by 2% to stabilize your target HR zone.'
    case 'indoor_bike':
      return 'Cardio Strain Running High. Drop bike resistance by 2 levels to stabilize your target HR zone.'
    case 'rower':
    case 'ski_erg':
      return 'Cardio Strain Running High. Ease your stroke rate and damper to stabilize your target HR zone.'
    default:
      return 'Cardio Strain Running High. Reduce intensity slightly to stabilize your target HR zone.'
  }
}

export class InterventionResolver {
  private latestHr: number | null = null
  private latestKind: MachineKind = 'unknown'
  private timer: ReturnType<typeof setInterval> | null = null
  private listeners = new Set<InterventionListener>()
  private lastFiredAt = 0
  /** Re-arm window so a sustained spike doesn't spam the overlay every second. */
  private readonly cooldownMs = 20000
  private targetHr: number

  constructor(targetHr: number) {
    this.targetHr = targetHr
  }

  setTargetHr(targetHr: number) {
    this.targetHr = targetHr
  }

  onIntervention(listener: InterventionListener): () => void {
    this.listeners.add(listener)
    return () => this.listeners.delete(listener)
  }

  /** Feed each live telemetry frame in; HR/machine kind are cached for eval. */
  ingest(frame: TelemetryFrame) {
    if (frame.metrics.heart_rate_bpm != null) this.latestHr = frame.metrics.heart_rate_bpm
    if (frame.machineKind !== 'unknown') this.latestKind = frame.machineKind
  }

  start() {
    if (this.timer) return
    this.timer = setInterval(() => this.evaluate(), EVAL_INTERVAL_MS)
  }

  stop() {
    if (this.timer) clearInterval(this.timer)
    this.timer = null
  }

  private evaluate() {
    if (this.latestHr == null || this.targetHr <= 0) return
    const delta = this.latestHr - this.targetHr
    if (delta < HR_INTERVENTION_DELTA_BPM) return

    const now = Date.now()
    if (now - this.lastFiredAt < this.cooldownMs) return
    this.lastFiredAt = now

    const event: InterventionEvent = {
      kind: 'cardio_strain_high',
      observedHr: this.latestHr,
      targetHr: this.targetHr,
      deltaBpm: delta,
      suggestion: suggestionForMachine(this.latestKind),
      machineKind: this.latestKind,
      firedAt: now,
    }
    for (const l of this.listeners) l(event)
  }
}
